import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, ShoppingBag, Zap } from 'lucide-react';
import { SoundManager } from '../utils/SoundManager';

interface CheckoutModalProps {
  isOpen: boolean;
  productName: string;
  originalPrice?: string;
  finalPrice: string;
  onConfirm: () => void;
  onClose: () => void;
}

export const CheckoutModal = ({ isOpen, productName, originalPrice, finalPrice, onConfirm, onClose }: CheckoutModalProps) => {
  useEffect(() => {
    if (isOpen) SoundManager.play('cha_ching');
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="checkout-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={onClose}
        >
          <motion.div
            key="checkout-panel"
            initial={{ opacity: 0, scale: 0.85, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.85, y: 40 }}
            transition={{ type: 'spring', stiffness: 220, damping: 22 }}
            onClick={e => e.stopPropagation()}
            className="relative w-full max-w-md bg-gradient-to-br from-[#0a0a0a] to-[#0d1a1a] border border-cyan-500/40 rounded-3xl p-8 shadow-[0_0_80px_rgba(6,182,212,0.25)] overflow-hidden"
          >
            {/* Animated corner accents */}
            <div className="absolute top-0 left-0 w-16 h-16 border-t-2 border-l-2 border-cyan-400/60 rounded-tl-3xl pointer-events-none" />
            <div className="absolute bottom-0 right-0 w-16 h-16 border-b-2 border-r-2 border-cyan-400/60 rounded-br-3xl pointer-events-none" />

            {/* Glow blob */}
            <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />

            {/* Close */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-slate-500 hover:text-white transition-colors p-1.5 rounded-full hover:bg-white/10"
            >
              <X size={16} />
            </button>

            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-11 h-11 rounded-2xl bg-cyan-500/20 border border-cyan-400/50 flex items-center justify-center shrink-0 shadow-[0_0_15px_rgba(6,182,212,0.3)]">
                <ShoppingBag size={20} className="text-cyan-400" />
              </div>
              <div>
                <p className="text-[10px] font-mono text-cyan-500/60 uppercase tracking-widest">Luna Commerce</p>
                <p className="text-base font-bold text-cyan-300 tracking-wide">Checkout</p>
              </div>
            </div>

            {/* Product name */}
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-5 leading-snug break-words">
              {productName || 'Selected Product'}
            </h2>

            {/* Price display */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-6 flex items-center justify-between gap-4">
              <div className="flex flex-col gap-1">
                {originalPrice && (
                  <span className="text-sm text-slate-500 line-through font-mono">
                    {originalPrice}
                  </span>
                )}
                <motion.span
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                  className="text-3xl sm:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-emerald-400 drop-shadow-[0_0_20px_rgba(6,182,212,0.8)]"
                >
                  {finalPrice}
                </motion.span>
                {originalPrice && (
                  <span className="text-[10px] font-mono text-emerald-400/80 uppercase tracking-widest">
                    ✓ Luna-negotiated price
                  </span>
                )}
              </div>
              <motion.div
                animate={{ rotate: [0, -10, 10, -10, 0] }}
                transition={{ delay: 0.4, duration: 0.5 }}
                className="text-4xl select-none"
              >
                💰
              </motion.div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <motion.button
                whileTap={{ scale: 0.96 }}
                whileHover={{ scale: 1.02 }}
                onClick={onConfirm}
                className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-400 hover:to-emerald-400 text-black font-black py-3.5 rounded-xl text-xs uppercase tracking-widest shadow-[0_0_30px_rgba(6,182,212,0.4)] hover:shadow-[0_0_50px_rgba(6,182,212,0.7)] transition-all"
              >
                <Zap size={14} />
                Confirm Purchase
              </motion.button>
              <button
                onClick={onClose}
                className="px-5 py-3.5 border border-white/10 rounded-xl text-white/50 hover:bg-white/5 hover:text-white transition-all text-xs uppercase tracking-widest font-bold"
              >
                Cancel
              </button>
            </div>

            <p className="text-center text-[9px] font-mono text-slate-600 mt-4 tracking-wider uppercase">
              Powered by Luna AI Commerce Engine — Kangavena.com
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
